import { AxiosRequestConfig, AxiosResponse, Method, isCancel } from 'axios';

import type { TranslationString } from 'shared/entities/localization';

import {
  BaseResponseCode,
  type ApiCallErrorRaw,
  type ApiCallResponse,
  type ApiParams,
  type BaseResponseCodeType
} from '../client';

import { callApi } from './api';
import { configApiCall } from './helpers';

export type BinaryApiSuccess = { blob: Blob };

/** Client-only: HTTP success but response body is not a Blob (binary contract violated). */
export const INVALID_BINARY_RESPONSE_CODE = 'invalid_binary_response' as const;

/** Client-only: no HTTP response (network error, connection reset, etc.). */
export const BINARY_NETWORK_ERROR_CODE = 'network_error' as const;

/**
 * Domain error codes from the endpoint plus client-only binary transport codes.
 */
export type BinaryApiErrorCode<Error extends string = string> =
  | Error
  | typeof INVALID_BINARY_RESPONSE_CODE
  | typeof BINARY_NETWORK_ERROR_CODE;

/**
 * Same as {@link ApiParams} but error codes include {@link BinaryApiErrorCode} (e.g. invalid binary body).
 */
export type ApiBlobParams<ErrorCode extends string = string> = Omit<
  ApiParams<ErrorCode>,
  'errorsMap' | 'expectedErrorCodes'
> & {
  errorsMap?: (code: BinaryApiErrorCode<ErrorCode>) => TranslationString;
  expectedErrorCodes?: BinaryApiErrorCode<ErrorCode>[];
};

const isUnknownRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const parseJsonObjectFromText = (
  text: string
): Record<string, unknown> | null => {
  try {
    const parsed: unknown = JSON.parse(text);
    return isUnknownRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

type BinaryErrorBody<
  ErrorCode extends string,
  ErrorData extends Record<string, any>
> = {
  status: number;
} & Omit<ApiCallErrorRaw<BinaryApiErrorCode<ErrorCode>, ErrorData>, 'status'>;

const buildBinaryHttpErrorData = <
  ErrorCode extends string,
  ErrorData extends Record<string, any>
>(
  httpStatus: number,
  payload: Record<string, unknown>
): BinaryErrorBody<ErrorCode, ErrorData> => {
  const codeRaw = payload.code;
  const messageRaw = payload.message;
  const dataRaw = payload.data;

  const message =
    typeof messageRaw === 'string' && messageRaw.length > 0
      ? messageRaw
      : 'Request failed';

  const data = (isUnknownRecord(dataRaw) ? dataRaw : {}) as ErrorData;

  if (typeof codeRaw === 'string' && codeRaw.length > 0) {
    return {
      status: httpStatus,
      code: codeRaw as BaseResponseCodeType<BinaryApiErrorCode<ErrorCode>>,
      message,
      data
    };
  }

  return {
    status: httpStatus,
    code: BaseResponseCode.badRequest as BaseResponseCodeType<
      BinaryApiErrorCode<ErrorCode>
    >,
    message,
    data
  };
};

/**
 * POST/GET with raw binary body (e.g. PDF). Does not unwrap JSON envelope like {@link api}.
 * On HTTP errors, parses JSON from response body when axios delivers it as Blob.
 */
export const requestBinaryApi = async <
  ErrorCode extends string = string,
  ErrorData extends Record<string, any> = Record<string, any>
>(
  endpoint: string,
  methodType: Method = 'GET',
  rawData: Record<string, any> = {},
  config: AxiosRequestConfig = {},
  multipartFormData = false
): Promise<
  ApiCallResponse<BinaryApiSuccess, BinaryApiErrorCode<ErrorCode>, ErrorData>
> => {
  const actualConfig = configApiCall(
    methodType,
    rawData,
    { ...config, responseType: 'blob' },
    multipartFormData
  );

  const response = await callApi<
    Blob,
    ApiCallErrorRaw<BinaryApiErrorCode<ErrorCode>, ErrorData>
  >(endpoint, methodType, actualConfig);

  if (!response.isError) {
    const axiosResponse: AxiosResponse<Blob> = response.data;
    const body = axiosResponse.data;

    if (!(body instanceof Blob)) {
      return {
        isError: true,
        isCancelled: false,
        data: {
          status: axiosResponse.status,
          code: INVALID_BINARY_RESPONSE_CODE as BaseResponseCodeType<
            BinaryApiErrorCode<ErrorCode>
          >,
          message: 'Expected binary response body',
          data: {} as ErrorData
        }
      };
    }

    return {
      isError: false,
      status: axiosResponse.status,
      data: { blob: body },
      rawData: body
    };
  } else {
    const err = response.data;

    if (isCancel(err)) {
      return {
        isError: true,
        isCancelled: true,
        data: null
      };
    }

    const ar = err.response;
    if (!ar) {
      return {
        isError: true,
        isCancelled: false,
        data: {
          status: 0,
          code: BINARY_NETWORK_ERROR_CODE as BaseResponseCodeType<
            BinaryApiErrorCode<ErrorCode>
          >,
          message: 'Network request failed',
          data: {} as ErrorData
        }
      };
    }

    let payload: Record<string, unknown> = {};
    const raw = ar.data;

    if (raw instanceof Blob) {
      const text = await raw.text();
      const parsed = parseJsonObjectFromText(text);
      if (parsed) {
        payload = parsed;
      }
    } else if (isUnknownRecord(raw)) {
      payload = raw;
    }

    return {
      isError: true,
      isCancelled: false,
      data: buildBinaryHttpErrorData<ErrorCode, ErrorData>(ar.status, payload)
    };
  }
};
